/**
 * Softphone AGORA (pilote Amazon Connect, docs/TELEPHONIE.md) : le fournisseur (moteur, état de l'agent et de l'appel,
 * CCP masqué, annonce des appels entrants) monté par la coquille, et le panneau « Téléphone » du dock (bouton
 * Téléphone : DockAgora) – numéro de la ligne (« Mon numéro »), disponibilité, champ unique « Rechercher un contact ou
 * numéro à appeler » (ChampAppel), renvoi d'appel vers un autre numéro et, en face, « Journal des appels » (l'écran
 * Appels, hors des menus : HORS_MENU du catalogue), appel avec le correspondant reconnu (contact, tiers :
 * GET /v1/service/appels/identification) et « Ouvrir la fiche », muet, attente, clavier, transfert, raccrocher.
 * Appel entrant ou appel en un clic : le panneau du dock s'ouvre (évènement EVENEMENT_TELEPHONE) ; un appel en un
 * clic quand le téléphone n'est pas prêt (connexion, appel en cours) pré-remplit le numéro à appeler.
 * Actif pour les agents du pilote (acces.ts : groupe Entra du softphone de l'environnement, droit « lire » sur
 * service.telephonie.appels) qui l'ont activé dans Réglages › Téléphone ; jamais sur un écran étroit (Connect ne prend
 * pas en charge les navigateurs mobiles). En maquette : moteur simulé, bouton « Simuler un appel entrant ». Les numéros
 * d'AGORA (LienTelephone) composent par le softphone quand il est prêt (useSoftphone), sinon ils restent des liens `tel:`.
 * Journal : en maquette, le panneau écrit chaque appel terminé (source « simulation ») ; avec Connect, le serveur
 * l'écrit à partir des événements de fin d'appel. Les listes d'appels sont rechargées après chaque appel.
 * Suivi de l'appel (SuiviAppel.tsx) : bouton « REC » (enregistrement, rouge par défaut) et menu « Affecter », pour
 * l'appel en cours puis le dernier appel ; choix envoyés à l'API (contact Connect de l'appel).
 * Un seul onglet d'AGORA fait tourner le téléphone (verrou entre onglets, useVerrouOnglet) : les autres affichent « Dans
 * un autre onglet » et le bouton « Utiliser ici », et prennent le relais quand cet onglet se ferme. Un appel en cours
 * (useActiviteEnCours) fait demander confirmation avant de fermer l'onglet ou de quitter AGORA.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RESSOURCE_APPELS, TOUTES_FILIALES, filiale as trouverFiliale, noeud, numeroInternational,
  type EtatAppelSoftphone, type IdentificationAppel, type JournalAppel, type RessourceLieeAppel,
} from '@agora/shared';
import {
  Button, SegmentedControl, Skeleton, SkeletonGroup, StatusBadge, TextField, cx, type Status,
} from '../../../design';
import { useConfirmation, useDroits, useT } from '../../../socle';
import { useSocle } from '../../contexte';
import { useActionsEspace } from '../../pages/espace';
import { useActiviteEnCours } from '../../pages/sortie';
import { useVerrouOnglet } from '../../pages/verrou-onglet';
import { nomContactAppel } from '../AppelsObjet';
import { agentDuPilote } from './acces';
import {
  ETAT_INITIAL, creerMoteurAilleurs, type AppelSoftphone, type EtatAgent, type EtatSoftphone, type MoteurSoftphone,
} from './moteur';
import { creerMoteurMaquette, type AppelTermine, type MoteurMaquette } from './moteur-maquette';
import { creerMoteurConnect } from './moteur-connect';
import { SuiviDeLAppel, type SuiviAppel } from './SuiviAppel';

export interface ContexteSoftphone {
  etat: EtatSoftphone;
  /** Agent connecté et aucun appel en cours : un numéro d'AGORA peut être composé. */
  pret: boolean;
  /**
   * Compose un numéro E.164 et ouvre le panneau Téléphone du dock ; téléphone pas prêt : ouvre le panneau avec le
   * numéro pré-rempli (numeroPropose).
   */
  appeler: (numero: string) => void;
  /** Numéro à pré-remplir dans le champ « Numéro à appeler », lu une fois par le panneau (retirerPropose). */
  numeroPropose: string | null;
  retirerPropose: () => void;
  moteur: MoteurSoftphone;
  /** Filiale des appels (identification du correspondant, journal de la maquette). */
  filiale: string;
  /** Numéro de la ligne (E.164), affiché « Mon numéro » ; absent si l'instance n'en a pas. */
  numeroLigne?: string;
  maquette: boolean;
  /** Correspondant de l'appel en cours (nom, détail, fiche). */
  correspondant: Correspondant;
  /** Appel en cours, puis dernier appel (bouton d'enregistrement, objets affectés) ; null sans appel. */
  suivi: SuiviAppel | null;
  /** Bouton « REC » de l'appel en cours : rouge ↔ gris. */
  basculerEnregistrement: () => void;
  /** Affecte l'appel suivi à un objet (actif), ou retire l'affectation. */
  affecter: (ressource: RessourceLieeAppel, objetId: string, libelle: string, actif: boolean) => Promise<void>;
  /** Masque le dernier appel. */
  oublierAppel: () => void;
  /** Téléphone ouvert dans un autre onglet (agent `ailleurs`) : le reprend dans cet onglet. */
  utiliserIci: () => void;
}

/** Verrou du téléphone entre les onglets d'AGORA (Web Locks) : un seul onglet a le moteur et reçoit les appels. */
const VERROU_TELEPHONE = 'agora:softphone';

/** Ouvre le panneau Téléphone du dock (appel qui sonne, appel en un clic) : DockAgora l'écoute. */
export const EVENEMENT_TELEPHONE = 'agora:telephone';
const ouvrirPanneau = () => window.dispatchEvent(new Event(EVENEMENT_TELEPHONE));

const Ctx = React.createContext<ContexteSoftphone | null>(null);

/** Softphone de la page, ou null (pas de softphone pour cet utilisateur ou cet environnement). */
export function useSoftphone(): ContexteSoftphone | null {
  return React.useContext(Ctx);
}

/** Numéro que le correspondant d'un appel de démonstration compose (Claire Exemple, contact du GAEC des Saules). */
export const NUMERO_DEMO_MAQUETTE = '+33475000001';
/** Numéro (fictif) de la ligne de la maquette, affiché « Mon numéro ». */
export const NUMERO_LIGNE_MAQUETTE = '+33465710100';
/** Délai avant de relire les listes d'appels : le journal Connect est écrit à partir d'un événement asynchrone. */
const RELECTURE_APPELS_MS = 5000;

/** Statut affiché de l'agent (badge du panneau, pastille du bouton du dock). */
export const STATUT_AGENT: Record<EtatAgent, Status> = {
  connexion: 'waiting', deconnecte: 'danger', disponible: 'success', absent: 'neutral', occupe: 'progress', erreur: 'danger',
  ailleurs: 'neutral',
};

/** Amazon Connect ne prend pas en charge les navigateurs mobiles : pas de softphone sur un écran étroit. */
const ECRAN_SOFTPHONE = '(min-width: 641px)';

export function Softphone({ children }: { children: React.ReactNode }) {
  const { config, moi, api, filiale, filialesAutorisees, preferences } = useSocle();
  const droits = useDroits(RESSOURCE_APPELS);
  const queryClient = useQueryClient();
  // Droit acquis une fois pour toutes : changer de filiale ne coupe pas un appel en cours.
  const [autorise, setAutorise] = React.useState(false);
  React.useEffect(() => { if (droits.lire) setAutorise(true); }, [droits.lire]);
  const [ecranLarge] = React.useState(() => window.matchMedia?.(ECRAN_SOFTPHONE).matches ?? true);
  const actif = autorise && ecranLarge && agentDuPilote(config, moi.groupes) && !!preferences.softphone;

  // Filiale des appels : celle de l'instance Connect (journal côté serveur), sinon la filiale affichée.
  const filialeAppels = config.softphone?.filiale ?? (filiale !== TOUTES_FILIALES ? filiale : filialesAutorisees[0] ?? 'FR01');
  const contexte = React.useRef({ api, moi, filialeAppels });
  contexte.current = { api, moi, filialeAppels };

  const ccp = React.useRef<HTMLDivElement>(null);
  const [moteur, setMoteur] = React.useState<MoteurSoftphone | null>(null);
  const [etat, setEtat] = React.useState<EtatSoftphone>(ETAT_INITIAL);
  const correspondant = useCorrespondant(etat.appel, filialeAppels);
  const { t } = useT();
  const verrou = useVerrouOnglet(VERROU_TELEPHONE, actif);
  const detenu = verrou.etat === 'detenu';
  useActiviteEnCours('appel', !!etat.appel && etat.appel.etat !== 'termine');

  // Téléphone ouvert dans un autre onglet : moteur inerte (état « Dans un autre onglet »), jusqu'au verrou.
  React.useEffect(() => {
    if (!actif || verrou.etat !== 'ailleurs') return;
    const m = creerMoteurAilleurs();
    setMoteur(m);
    const desabonner = m.abonner(setEtat);
    return () => {
      desabonner();
      setMoteur(null);
      setEtat(ETAT_INITIAL);
    };
  }, [actif, verrou.etat]);

  React.useEffect(() => {
    if (!actif || !detenu || !ccp.current) return;
    const journaliser = (a: AppelTermine) => {
      const { api: client, moi: utilisateur, filialeAppels: f } = contexte.current;
      const journal: JournalAppel = {
        filiale: f, source: 'simulation', idExterne: `softphone-${a.debut}`, sens: a.sens, numeroExterne: a.numero,
        collaborateur: utilisateur.email, debut: a.debut, dureeS: a.dureeS, statut: a.statut,
      };
      client.post('/v1/service/appels', journal).catch(() => undefined);
    };
    const m = config.softphone && !config.maquette ? creerMoteurConnect(config.softphone) : creerMoteurMaquette(journaliser);
    setMoteur(m);
    const desabonner = m.abonner(setEtat);
    m.demarrer(ccp.current);
    return () => {
      desabonner();
      m.arreter();
      setMoteur(null);
      setEtat(ETAT_INITIAL);
    };
  }, [actif, detenu, config]);

  // Appel entrant : le panneau Téléphone du dock s'ouvre. Fin d'appel : listes d'appels rechargées (journal).
  const etatAppel = etat.appel?.etat;
  React.useEffect(() => {
    if (etatAppel === 'sonnerie') ouvrirPanneau();
    if (etatAppel !== 'termine') return;
    const id = window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['appels'] }), config.maquette ? 300 : RELECTURE_APPELS_MS);
    return () => window.clearTimeout(id);
  }, [etatAppel, queryClient, config.maquette]);

  // Zone d'annonce toujours présente (panneau ouvert ou non) : un lecteur d'écran ne lit que les changements d'une zone
  // déjà affichée.
  const annonce = etat.appel?.etat === 'sonnerie' && etat.appel.sens === 'entrant'
    ? t('service.telephonie.softphone.annonceEntrant', { correspondant: correspondant.titre }) : '';

  const suiviAppel = useSuiviAppel(etat, correspondant, contexte, queryClient);

  const pret = !!moteur && (etat.agent === 'disponible' || etat.agent === 'absent') && !etat.appel;
  const [numeroPropose, setNumeroPropose] = React.useState<string | null>(null);
  const retirerPropose = React.useCallback(() => setNumeroPropose(null), []);
  const numeroLigne = config.softphone?.numero ?? (config.maquette ? NUMERO_LIGNE_MAQUETTE : undefined);
  const valeur = React.useMemo<ContexteSoftphone | null>(() => (moteur ? {
    etat, pret, moteur, filiale: filialeAppels, numeroLigne, maquette: config.maquette, correspondant, numeroPropose, retirerPropose,
    ...suiviAppel,
    appeler: (numero) => {
      if (pret) moteur.appeler(numero);
      else setNumeroPropose(numero);
      ouvrirPanneau();
    },
    utiliserIci: verrou.prendre,
  } : null), [moteur, etat, pret, filialeAppels, numeroLigne, config.maquette, correspondant, numeroPropose, retirerPropose, suiviAppel, verrou.prendre]);

  return (
    <Ctx.Provider value={valeur}>
      {children}
      {actif ? (
        <>
          <div ref={ccp} className="agx-softphone-ccp" aria-hidden="true" />
          <p className="agx-annonce" aria-live="assertive" aria-atomic="true">{annonce}</p>
        </>
      ) : null}
    </Ctx.Provider>
  );
}

type ContexteApi = { readonly current: { api: ReturnType<typeof useSocle>['api']; filialeAppels: string } };
type ChoixSuivi = Pick<ContexteSoftphone, 'suivi' | 'basculerEnregistrement' | 'affecter' | 'oublierAppel'>;

/**
 * Suivi de l'appel (bouton « REC », objets affectés) : commence avec l'appel (identifiant = contact Connect), reste après
 * lui (dernier appel) jusqu'au suivant. Bouton gris avant le décroché : renvoyé une fois l'agent relié (Connect ne
 * suspend qu'un enregistrement démarré).
 */
function useSuiviAppel(etat: EtatSoftphone, correspondant: Correspondant, contexte: ContexteApi, queryClient: ReturnType<typeof useQueryClient>): ChoixSuivi {
  const [suivi, setSuivi] = React.useState<SuiviAppel | null>(null);
  const courant = React.useRef(suivi);
  courant.current = suivi;
  const appel = React.useRef(etat.appel);
  appel.current = etat.appel;
  const appelId = etat.appel?.id;

  React.useEffect(() => {
    if (!appelId) {
      setSuivi((s) => (s && !s.termine ? { ...s, termine: true } : s));
      return;
    }
    setSuivi((s) => (s?.contactId === appelId ? s : {
      contactId: appelId, numero: appel.current?.numero ?? null, debut: new Date().toISOString(), titre: '', tiers: null,
      contact: null, enregistrement: true, affectations: [], termine: false,
    }));
  }, [appelId]);

  // Correspondant reconnu pendant l'appel : gardé pour le dernier appel (menu « Affecter », formulaires préremplis).
  React.useEffect(() => {
    setSuivi((s) => (s && !s.termine && s.contactId === appel.current?.id ? {
      ...s, numero: appel.current?.numero ?? s.numero, titre: correspondant.titre, tiers: correspondant.tiers, contact: correspondant.contact,
    } : s));
  }, [correspondant]);

  const envoyerEnregistrement = React.useCallback((contactId: string, actif: boolean) => {
    const { api, filialeAppels } = contexte.current;
    return api.put<EtatAppelSoftphone>(`/v1/service/appels/softphone/${encodeURIComponent(contactId)}/enregistrement`, { filiale: filialeAppels, actif });
  }, [contexte]);

  const relie = etat.appel?.etat === 'en-cours';
  React.useEffect(() => {
    const s = courant.current;
    if (relie && s && !s.enregistrement && s.contactId === appelId) envoyerEnregistrement(s.contactId, false).catch(() => undefined);
  }, [relie, appelId, envoyerEnregistrement]);

  const basculerEnregistrement = React.useCallback(() => {
    const s = courant.current;
    if (!s || s.termine) return;
    const actif = !s.enregistrement;
    setSuivi({ ...s, enregistrement: actif, erreur: undefined });
    envoyerEnregistrement(s.contactId, actif)
      .then((r) => setSuivi((x) => (x?.contactId === r.contactId ? { ...x, enregistrement: r.enregistrement } : x)))
      .catch(() => setSuivi((x) => (x?.contactId === s.contactId ? { ...x, enregistrement: !actif, erreur: 'enregistrement' } : x)));
  }, [envoyerEnregistrement]);

  const affecter = React.useCallback(async (ressource: RessourceLieeAppel, objetId: string, libelle: string, actif: boolean) => {
    const s = courant.current;
    if (!s) return;
    const { api, filialeAppels } = contexte.current;
    try {
      const r = await api.put<EtatAppelSoftphone>(
        `/v1/service/appels/softphone/${encodeURIComponent(s.contactId)}/affectations`, { filiale: filialeAppels, ressource, objetId, actif });
      setSuivi((x) => (x?.contactId === r.contactId ? {
        ...x, erreur: undefined,
        affectations: r.affectations.map((a) => ({
          ...a,
          libelle: a.ressource === ressource && a.objetId === objetId ? libelle : x.affectations.find((b) => b.ressource === a.ressource && b.objetId === a.objetId)?.libelle,
        })),
      } : x));
      void queryClient.invalidateQueries({ queryKey: ['appels'] });
    } catch {
      setSuivi((x) => (x?.contactId === s.contactId ? { ...x, erreur: 'affectation' } : x));
    }
  }, [contexte, queryClient]);

  const oublierAppel = React.useCallback(() => setSuivi((s) => (s?.termine ? null : s)), []);
  return React.useMemo(() => ({ suivi, basculerEnregistrement, affecter, oublierAppel }), [suivi, basculerEnregistrement, affecter, oublierAppel]);
}

/** Correspondant reconnu d'un appel : nom (contact, sinon tiers), détail et fiche à ouvrir. */
function useCorrespondant(appel: AppelSoftphone | null, filiale: string) {
  const { t } = useT();
  const { api } = useSocle();
  const numero = appel?.numero ?? null;
  const { data: ident } = useQuery({
    queryKey: ['softphone', 'identification', filiale, numero],
    enabled: !!numero,
    staleTime: 60_000,
    queryFn: ({ signal }) => api.get<IdentificationAppel>('/v1/service/appels/identification', { filiale, numero: numero ?? '' }, signal),
  });
  return React.useMemo(() => {
    const nom = ident?.contact ? nomContactAppel(ident.contact) : ident?.tiers?.raisonSociale;
    return {
      titre: nom ?? numero ?? t('service.telephonie.numeroMasque'),
      detail: [ident?.contact ? ident.tiers?.raisonSociale : undefined, nom ? numero : undefined].filter(Boolean).join(' · '),
      route: ident?.contact ? `/referentiels/contacts/${encodeURIComponent(ident.contact.id)}`
        : ident?.tiers ? `/referentiels/tiers/${encodeURIComponent(ident.tiers.id)}` : undefined,
      tiers: ident?.tiers ?? null,
      contact: ident?.contact ?? null,
    };
  }, [ident, numero, t]);
}

type Correspondant = ReturnType<typeof useCorrespondant>;

const estMaquette = (m: MoteurSoftphone): m is MoteurMaquette => 'simulerAppelEntrant' in m;

/**
 * Panneau « Téléphone » du dock (bouton Téléphone de DockAgora, panneau Radix du dock : Échap ou clic à côté pour fermer,
 * focus rendu au bouton) : état de l'agent, puis selon le cas connexion, numérotation ou appel en cours.
 */
export function PanneauSoftphone({ fermer }: { fermer: () => void }) {
  const { t } = useT();
  const confirmer = useConfirmation();
  const s = useSoftphone();
  if (!s) return null;
  const { moteur, etat, filiale, numeroLigne, correspondant } = s;
  const appel = etat.appel;
  const utiliserIci = async () => {
    if (await confirmer({
      titre: t('service.telephonie.softphone.utiliserIciTitre'), texte: t('service.telephonie.softphone.utiliserIciTexte'),
      confirmer: t('service.telephonie.softphone.utiliserIci'),
    })) s.utiliserIci();
  };

  let contenu: React.ReactNode;
  if (appel) contenu = <AppelEnCours moteur={moteur} appel={appel} filiale={filiale} correspondant={correspondant} renvoi={etat.renvoi} />;
  else if (etat.agent === 'ailleurs') {
    contenu = (
      <>
        <p className="agx-softphone-aide">{t('service.telephonie.softphone.ailleursAide')}</p>
        <Button variant="primary" icon="telephone" onClick={() => void utiliserIci()}>{t('service.telephonie.softphone.utiliserIci')}</Button>
      </>
    );
  } else if (etat.agent === 'connexion') {
    contenu = (
      <SkeletonGroup label={t('service.telephonie.softphone.connexionEnCours')}>
        <Skeleton width={60} />
        <Skeleton variant="block" size="sm" />
      </SkeletonGroup>
    );
  } else if (etat.agent === 'deconnecte' || etat.agent === 'erreur') {
    contenu = (
      <>
        <p className="agx-softphone-aide">{t('service.telephonie.softphone.deconnecteAide')}</p>
        <Button variant="primary" icon="telephone" onClick={() => moteur.connecter()}>{t('service.telephonie.softphone.seConnecter')}</Button>
      </>
    );
  } else contenu = <Numerotation s={s} fermer={fermer} />;

  return (
    <section className={cx('agx-softphone-panneau', 'ag-glass', 'ag-glass-strong')} role="dialog" aria-label={t('service.telephonie.softphone.titre')}>
      <header className="agx-softphone-entete">
        <h2>{t('service.telephonie.softphone.titre')}</h2>
        <StatusBadge status={STATUT_AGENT[etat.agent]} label={t(`service.telephonie.softphone.etats.${etat.agent}`)} />
        <Button variant="quiet" size="sm" icon="close" aria-label={t('commun.fermer')} onClick={fermer} />
      </header>
      {numeroLigne ? (
        <p className="agx-softphone-ligne">{t('service.telephonie.softphone.monNumero')} <span className="ag-mono">{numeroLigne}</span></p>
      ) : null}
      {contenu}
      {/* Même place pendant et après l'appel : un formulaire de création ouvert survit à la fin de l'appel. */}
      {s.suivi && appel?.etat !== 'sonnerie' ? <SuiviDeLAppel s={s} suivi={s.suivi} /> : null}
      {etat.erreur ? <p className="agx-softphone-erreur" role="alert">{t(`service.telephonie.softphone.erreurs.${etat.erreur}`)}</p> : null}
    </section>
  );
}

/** Champ « Rechercher un contact ou numéro à appeler » : chargé à la demande (Downshift hors du premier chargement). */
const ChampAppel = React.lazy(() => import('./ChampAppel'));
/** Écran du journal des appels, ouvert par le bouton « Journal des appels » du panneau. */
const ROUTE_JOURNAL = noeud(RESSOURCE_APPELS)?.route ?? '/';

function Numerotation({ s, fermer }: { s: ContexteSoftphone; fermer: () => void }) {
  const { t } = useT();
  const espace = useActionsEspace();
  const { moteur, etat, filiale, maquette, numeroPropose, retirerPropose } = s;
  const disponible = etat.agent === 'disponible';
  const contacts = useDroits('contacts').lire;
  const [saisie, setSaisie] = React.useState('');
  const [erreur, setErreur] = React.useState<string>();
  const pays = trouverFiliale(filiale)?.pays ?? 'FR';
  // Appel en un clic quand le téléphone n'était pas prêt : le numéro attend dans le champ.
  React.useEffect(() => {
    if (!numeroPropose) return;
    setSaisie(numeroPropose);
    setErreur(undefined);
    retirerPropose();
  }, [numeroPropose, retirerPropose]);
  const appeler = (numero: string) => {
    setSaisie('');
    setErreur(undefined);
    moteur.appeler(numero);
  };
  // « Appeler » (ou Entrée, liste fermée) : le texte saisi doit être un numéro complet ; un contact se choisit dans la liste.
  const composer = (e: React.FormEvent) => {
    e.preventDefault();
    const numero = numeroInternational(saisie, pays);
    if (numero) appeler(numero);
    else setErreur(t('service.telephonie.softphone.numeroInvalide'));
  };
  return (
    <>
      <SegmentedControl
        compact ariaLabel={t('service.telephonie.softphone.disponibilite')} value={disponible ? 'disponible' : 'absent'}
        options={[
          { value: 'disponible', label: t('service.telephonie.softphone.etats.disponible') },
          { value: 'absent', label: t('service.telephonie.softphone.etats.absent') },
        ]}
        onChange={(v) => moteur.changerDisponibilite(v === 'disponible')}
      />
      {/* Liste ouverte : elle propose l'appel elle-même (« Appeler le … », contacts) ; « Appeler » sert liste fermée. */}
      <form className="agx-softphone-numerotation" onSubmit={composer} noValidate>
        <React.Suspense fallback={<Skeleton variant="block" size="sm" />}>
          <ChampAppel
            filiale={filiale} pays={pays} contacts={contacts} texte={saisie} erreur={erreur} appeler={appeler}
            onTexte={(v) => { setSaisie(v); setErreur(undefined); }}
          />
        </React.Suspense>
        <Button type="submit" variant="primary" icon="telephone">{t('service.telephonie.softphone.appeler')}</Button>
      </form>
      <Renvoi
        moteur={moteur} renvoi={etat.renvoi} pays={pays}
        enFace={(
          <Button variant="quiet" size="sm" icon="list" onClick={(e) => { espace.ouvrir(ROUTE_JOURNAL, { evenement: e }); fermer(); }}>
            {t('service.telephonie.softphone.journal')}
          </Button>
        )}
      />
      {maquette && estMaquette(moteur) ? (
        <Button variant="quiet" size="sm" disabled={!disponible} onClick={() => moteur.simulerAppelEntrant(NUMERO_DEMO_MAQUETTE)}>
          {t('service.telephonie.softphone.simuler')}
        </Button>
      ) : null}
    </>
  );
}

/**
 * Renvoi d'appel : les appels de l'agent sonnent sur un autre numéro (son portable…) tant qu'il est Disponible ; ses
 * appels sortants appellent d'abord ce numéro (Connect : téléphone fixe de l'agent, moteur-connect.ts). `enFace` : ce
 * qui se place sur la même ligne, à l'opposé (bouton « Journal des appels »).
 */
function Renvoi({ moteur, renvoi, pays, enFace }: { moteur: MoteurSoftphone; renvoi: string | null; pays: string; enFace: React.ReactNode }) {
  const { t } = useT();
  const [ouvert, setOuvert] = React.useState(false);
  const [saisie, setSaisie] = React.useState('');
  const [erreur, setErreur] = React.useState<string>();
  if (renvoi) {
    return (
      <div className="agx-softphone-renvoi">
        <div className="agx-softphone-options">
          <p>{t('service.telephonie.softphone.renvoiActif', { numero: renvoi })}</p>
          {enFace}
        </div>
        <Button size="sm" icon="close" onClick={() => moteur.definirRenvoi(null)}>{t('service.telephonie.softphone.arreterRenvoi')}</Button>
      </div>
    );
  }
  const activer = (e: React.FormEvent) => {
    e.preventDefault();
    const numero = numeroInternational(saisie, pays);
    if (!numero) { setErreur(t('service.telephonie.softphone.numeroInvalide')); return; }
    setErreur(undefined);
    setOuvert(false);
    moteur.definirRenvoi(numero);
  };
  return (
    <div className="agx-softphone-renvoi">
      <div className="agx-softphone-options">
        <Button variant="quiet" size="sm" icon="arrow" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
          {t('service.telephonie.softphone.renvoi')}
        </Button>
        {enFace}
      </div>
      {ouvert ? (
        <form className="agx-softphone-numerotation" onSubmit={activer} noValidate>
          <TextField
            type="tel" autoComplete="tel" inputMode="tel" label={t('service.telephonie.softphone.renvoiVers')} value={saisie}
            help={t('service.telephonie.softphone.renvoiAide')} onChange={(v) => { setSaisie(v); setErreur(undefined); }} error={erreur}
          />
          <Button type="submit" icon="arrow">{t('service.telephonie.softphone.activerRenvoi')}</Button>
        </form>
      ) : null}
    </div>
  );
}

const TOUCHES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

interface PropsAppel { moteur: MoteurSoftphone; appel: AppelSoftphone; filiale: string; correspondant: Correspondant; renvoi: string | null }

/** Appel renvoyé (téléphone fixe de l'agent) : on décroche sur ce téléphone, et le muet du navigateur n'y agit pas. */
function AppelEnCours({ moteur, appel, filiale, correspondant: { titre, detail, route }, renvoi }: PropsAppel) {
  const { t } = useT();
  const espace = useActionsEspace();
  const [outil, setOutil] = React.useState<'clavier' | 'transfert' | null>(null);
  const [saisie, setSaisie] = React.useState('');
  const [erreur, setErreur] = React.useState<string>();
  const pays = trouverFiliale(filiale)?.pays ?? 'FR';

  const enCours = appel.etat === 'en-cours';
  const transferer = (e: React.FormEvent) => {
    e.preventDefault();
    const numero = numeroInternational(saisie, pays);
    if (!numero) { setErreur(t('service.telephonie.softphone.numeroInvalide')); return; }
    moteur.transferer(numero);
  };

  return (
    <div className="agx-softphone-appel">
      <div className="agx-softphone-correspondant">
        <strong>{titre}</strong>
        {detail ? <span>{detail}</span> : null}
        <span className="agx-softphone-statut"><EtatAppelTexte appel={appel} /></span>
        {/* Bouton et non <LienPage> : le panneau est hors des pages (pas de routeur) ; ouverture selon la règle de
            l'espace de travail (clic = page active, Ctrl/⌘ + clic = nouvelle page). */}
        {route ? <Button variant="quiet" size="sm" icon="external" onClick={(e) => espace.ouvrir(route, { evenement: e })}>{t('service.telephonie.softphone.ouvrirFiche')}</Button> : null}
      </div>
      {appel.etat === 'sonnerie' ? (
        <>
          {renvoi ? <p className="agx-softphone-aide">{t('service.telephonie.softphone.repondreSur', { numero: renvoi })}</p> : null}
          <div className="agx-softphone-actions">
            {renvoi ? null : <Button variant="primary" icon="telephone" onClick={() => moteur.decrocher()}>{t('service.telephonie.softphone.decrocher')}</Button>}
            <Button variant="danger" icon="raccrocher" onClick={() => moteur.raccrocher()}>{t('service.telephonie.softphone.refuser')}</Button>
          </div>
        </>
      ) : appel.etat !== 'termine' ? (
        <>
          {enCours ? (
            <div className="agx-softphone-outils">
              {renvoi ? null : <Button size="sm" icon={appel.muet ? 'microCoupe' : 'micro'} aria-pressed={appel.muet} onClick={() => moteur.basculerMuet()}>{t('service.telephonie.softphone.muet')}</Button>}
              <Button size="sm" icon="pause" aria-pressed={appel.enAttente} onClick={() => moteur.basculerAttente()}>{t('service.telephonie.softphone.attente')}</Button>
              <Button size="sm" icon="grid" aria-pressed={outil === 'clavier'} onClick={() => setOutil((o) => (o === 'clavier' ? null : 'clavier'))}>{t('service.telephonie.softphone.clavier')}</Button>
              <Button size="sm" icon="arrow" aria-pressed={outil === 'transfert'} onClick={() => setOutil((o) => (o === 'transfert' ? null : 'transfert'))}>{t('service.telephonie.softphone.transferer')}</Button>
            </div>
          ) : null}
          {enCours && outil === 'clavier' ? (
            <div className="agx-softphone-clavier" role="group" aria-label={t('service.telephonie.softphone.clavier')}>
              {TOUCHES.map((k) => <Button key={k} size="sm" className="ag-mono" onClick={() => moteur.envoyerTouches(k)}>{k}</Button>)}
            </div>
          ) : null}
          {enCours && outil === 'transfert' ? (
            <form className="agx-softphone-numerotation" onSubmit={transferer} noValidate>
              <TextField
                type="tel" autoComplete="tel" inputMode="tel" label={t('service.telephonie.softphone.numeroTransfert')} value={saisie}
                onChange={(v) => { setSaisie(v); setErreur(undefined); }} error={erreur}
              />
              <Button type="submit" icon="arrow">{t('service.telephonie.softphone.confirmerTransfert')}</Button>
            </form>
          ) : null}
          <div className="agx-softphone-actions">
            <Button variant="danger" icon="raccrocher" onClick={() => moteur.raccrocher()}>{t('service.telephonie.softphone.raccrocher')}</Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** « Appel entrant », « Numérotation… », chronomètre (mm:ss), « En attente », « Appel terminé ». */
function EtatAppelTexte({ appel }: { appel: AppelSoftphone }) {
  const { t } = useT();
  const [maintenant, setMaintenant] = React.useState(() => Date.now());
  const enCours = appel.etat === 'en-cours';
  React.useEffect(() => {
    if (!enCours) return;
    const id = window.setInterval(() => setMaintenant(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [enCours]);
  if (appel.etat === 'sonnerie') return <>{t(appel.sens === 'entrant' ? 'service.telephonie.softphone.entrant' : 'service.telephonie.softphone.numerotation')}</>;
  if (appel.etat === 'numerotation') return <>{t('service.telephonie.softphone.numerotation')}</>;
  if (appel.etat === 'termine') return <>{t('service.telephonie.softphone.termine')}</>;
  if (appel.enAttente) return <>{t('service.telephonie.softphone.enAttente')}</>;
  const s = Math.max(0, Math.floor((maintenant - (appel.decrocheLe ?? maintenant)) / 1000));
  const chrono = [Math.floor(s / 60), s % 60].map((n) => String(n).padStart(2, '0')).join(':');
  return <span className="ag-mono">{chrono}</span>;
}
